import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../config/axios';
import { toastService } from '../../services/toast';
import { hasPermission } from '../../services/permissionsService';

const PrivateRoute = ({ children, requiredPermission }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  // الصفحات اللي محتاجة تسجيل دخول
  const protectedPaths = [
    '/checkout',
    '/payment',
    '/admin'
  ];

  // نتحقق إذا كان المسار الحالي محتاج تسجيل دخول
  const needsAuth = protectedPaths.some(path => location.pathname.startsWith(path));
  
  // إضافة مرجع لتتبع ما إذا كانت الصفحة مخفية
  const [lastAuthUpdate, setLastAuthUpdate] = useState(Date.now());

  useEffect(() => {
    // إضافة مستمع للمزامنة الصامتة بين التبويبات
    const handleSilentUpdate = (e) => {
      // تعيين التوقيت لتفعيل إعادة التحقق
      setLastAuthUpdate(Date.now());
    };
    
    // إضافة مستمع لحدث المزامنة الصامتة
    window.addEventListener('silentAuthUpdate', handleSilentUpdate);
    
    // إزالة المستمع عند تفكيك المكون
    return () => {
      window.removeEventListener('silentAuthUpdate', handleSilentUpdate);
    };
  }, []); // يتم تشغيله مرة واحدة فقط عند تركيب المكون

  useEffect(() => {
    // دالة التحقق من الصلاحيات وتسجيل الدخول
    const verifyAuth = async () => {
      try {
        // تشخيص - تخزين معلومات بداية التحقق
        const diagnosticKey = `auth_check_${Date.now()}`;
        const saveAuthDiagnostic = (step, details) => {
          localStorage.setItem(diagnosticKey, JSON.stringify({
            step,
            details,
            timestamp: new Date().toISOString(),
            url: window.location.href
          }));
        };

        saveAuthDiagnostic('1_checkAuthStarted', {
          needsAuth,
          requiredPermission,
          path: location.pathname
        });

        // إذا كان المسار لا يحتاج تسجيل دخول، نعديه على طول
        if (!needsAuth) {
          setIsAuthenticated(true);
          setIsLoading(false);
          return;
        }

        // التحقق من وجود توكن في sessionStorage فقط
        const token = sessionStorage.getItem('token');
        saveAuthDiagnostic('2_checkingToken', { hasToken: !!token });

        // إذا مفيش توكن = مش مسجل دخول
        if (!token) {
          // التحقق من وجود علامة انتظار مزامنة التبويبات
          const isPendingSync = sessionStorage.getItem('pendingAuthSync') === 'true';
          saveAuthDiagnostic('2_1_checkingPendingSync', { isPendingSync });
          
          if (isPendingSync) {
            // إذا كانت هناك عملية مزامنة جارية، لا نقوم بتوجيه المستخدم بعد
            
            // الاستمرار في تحميل الصفحة حتى تنتهي المزامنة
            const handleSyncComplete = () => {
              // إعادة التحقق بعد اكتمال المزامنة
              verifyAuth();
            };
            
            // الاستماع لحدث اكتمال المزامنة
            window.addEventListener('authSyncCompleted', handleSyncComplete, { once: true });
            
            // لا نتخذ أي قرار الآن، الانتظار لنتيجة المزامنة
            return;
          }
          
          // إذا لم تكن هناك مزامنة جارية ولا يوجد توكن، المستخدم غير مسجل دخول
          setIsAuthenticated(false);
          setIsLoading(false);
          localStorage.removeItem('user');
          return;
        }

        // التحقق من صلاحيات الوصول للمسار مباشرة من API
        const checkPermission = async () => {
          try {
            // إذا كان المسار لا يتطلب صلاحية محددة، نسمح بالوصول طالما المستخدم مسجل دخول
            if (!requiredPermission) {
              return true;
            }

            saveAuthDiagnostic('3_checkingPermission', { requiredPermission });

            // تشخيص مكثف لمساعدة التتبع
            localStorage.setItem('auth_diagnostic', JSON.stringify({
              token: !!token,
              userRoleId: sessionStorage.getItem('userRoleId'),
              requiredPermission,
              path: location.pathname,
              timestamp: new Date().toISOString()
            }));

            // استدعاء API مباشرة للتحقق من الصلاحية
            const hasAccess = await hasPermission(requiredPermission);

            saveAuthDiagnostic('4_permissionCheckResult', { hasAccess });
            return hasAccess;
          } catch (error) {
            // تسجيل الخطأ تم إيقافه
            return false;
          }
        };

        // تنفيذ التحقق ووضع النتيجة
        const isAuthorized = await checkPermission();
        setIsAuthenticated(isAuthorized);
      } catch (error) {
        // التحقق من وجود توكن في sessionStorage
        const token = sessionStorage.getItem('token');
        // تخزين معلومات الخطأ في localStorage
        localStorage.setItem('authError', JSON.stringify({
          message: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        }));

        // تم تسجيل الخطأ في localStorage بدلاً من الكونسول
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuth(); // تشغيل وظيفة التحقق من الصلاحيات
  }, [location, requiredPermission, needsAuth, lastAuthUpdate]); // إضافة lastAuthUpdate للاعتماديات

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // التمييز بين المستخدم الذي لا يملك توكن والمستخدم الذي ليس لديه صلاحية
  // التحقق من وجود التوكن (من sessionStorage فقط)
  const token = sessionStorage.getItem('token');
  
  // إذا لم يكن هناك توكن، نوجه للصفحة تسجيل الدخول
  if (needsAuth && !token) {
    const isAdminPath = location.pathname.startsWith('/admin');
    return <Navigate to={isAdminPath ? '/admin/login' : '/login'} state={{ from: location }} replace />;
  }
  
  // إذا كان هناك توكن لكن ليس لديه صلاحية، نظهر توست بدلاً من التوجيه
  if (needsAuth && token && !isAuthenticated && requiredPermission) {
    // إظهار توست مرة واحدة فقط
    if (!localStorage.getItem(`permission_toast_${requiredPermission}`)) {
      toastService.error(`عفواً، ليس لديك صلاحية الوصول إلى هذه الصفحة`);
      // وضع علامة لمنع ظهور التوست مرة أخرى للصلاحية نفسها خلال هذه الجلسة
      localStorage.setItem(`permission_toast_${requiredPermission}`, 'shown');
      
      // مسح العلامة بعد 5 ثواني حتى يمكن ظهور التوست مرة أخرى إذا حاول المستخدم مرة أخرى
      setTimeout(() => {
        localStorage.removeItem(`permission_toast_${requiredPermission}`);
      }, 5000);
    }
    
    // بدلاً من إرجاع Navigate، نعيد صفحة فارغة أو رسالة
    return <></>;
  }

  return children;
};

export default PrivateRoute;
